import { TiptapDisplay } from "@skyltmax/ds9"
import { type Extensions } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import React from "react"
import { type BlockProps } from "."
import { graphql, getFragment } from "@/api"
import StaticContent from "@/common/components/static_content"

const HeadingDocument = Document.extend({ content: "heading" })
const ParagraphDocument = Document.extend({ content: "paragraph+" })

export const cardHeadingStack: Extensions = [
  StarterKit.configure({
    document: false,
    bold: false,
    italic: false,
    heading: {
      levels: [3],
      HTMLAttributes: {
        class: "heading-three mb-3",
      },
    },
  }),
  HeadingDocument,
  Placeholder,
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const cardParagraphStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: {
      HTMLAttributes: {
        class: "mb-4 paragraph-sm",
      },
    },
  }),
  ParagraphDocument,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export const LinkCardsFragment = graphql(`
  fragment FrontpagesLinkCards on FrontpagesLinkCardBlock {
    id

    cards {
      id
      heading
      description
      buttonLabel
      buttonUrl
      imageAlt

      image {
        data
        width
        height
        pngDefault
        pngTwox
        webpDefault
        webpTwox
      }
    }
  }
`)

const LinkCardsBlock: React.FC<BlockProps<typeof LinkCardsFragment>> = ({ blockable, index }) => {
  const block = getFragment(LinkCardsFragment, blockable)
  const cards = block.cards

  return (
    <StaticContent className="container grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
      {cards.map((card, i) => (
        <div key={card.id} className="relative z-0 min-h-[384px] overflow-hidden rounded-2xl bg-white-5">
          <div className="max-w-xs px-4 py-8 lg:px-8">
            <TiptapDisplay extensions={cardHeadingStack} json={card.heading} />
            <TiptapDisplay extensions={cardParagraphStack} json={card.description} />

            <a href={card.buttonUrl} className="button button-primary mb-0" data-gtm={`fp-link-card-${index}-${i}`}>
              {card.buttonLabel}
            </a>
          </div>

          <div className="bottom-0 right-0 -z-10 float-right h-48 w-48 lg:h-[248px] lg:w-[248px] xl:absolute">
            <picture>
              <source srcSet={`${card.image.webpDefault}, ${card.image.webpTwox} 2x`} type="image/webp" />
              <img
                srcSet={`${card.image.pngDefault}, ${card.image.pngTwox} 2x`}
                width={card.image.width || 0}
                height={card.image.height || 0}
                alt={card.imageAlt || ""}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </picture>
          </div>
        </div>
      ))}
    </StaticContent>
  )
}

export default LinkCardsBlock
